import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Brain,
  Layers,
  Shield,
  CheckCircle2,
  Loader2,
  ArrowRight,
  FileSearch,
  Cpu,
  Network,
} from 'lucide-react';

interface Props {
  onComplete: () => void;
}

interface ProcessingStage {
  id: string;
  title: string;
  subtitle: string;
  icon: typeof Brain;
  status: 'pending' | 'processing' | 'complete';
  progress: number;
  details: string[];
  currentDetail: number;
}

export function AICustomizationEngine({ onComplete }: Props) {
  const [stages, setStages] = useState<ProcessingStage[]>([
    {
      id: 'dictation',
      title: 'Learning Dictation Style',
      subtitle: 'Mapping surgeon\'s unique linguistic patterns',
      icon: Brain,
      status: 'pending',
      progress: 0,
      details: [
        'Parsing operative note structure...',
        'Identifying procedure-specific terminology...',
        'Mapping dictation patterns for Total Knee Arthroplasty...',
        'Mapping dictation patterns for Shoulder Arthroscopy...',
        'Building surgeon-specific phrase library...',
        'Correlating dictation style with CPT outcomes...',
        'Dictation model trained — 94% pattern confidence',
      ],
      currentDetail: 0,
    },
    {
      id: 'bundles',
      title: 'Building CPT Bundles',
      subtitle: 'Clustering common code combinations from case history',
      icon: Layers,
      status: 'pending',
      progress: 0,
      details: [
        'Analyzing CPT frequency across 100 historical cases...',
        'Clustering CPT 27447 with common secondary codes...',
        'Identifying modifier patterns (-59 on ancillary codes)...',
        'Building "Shoulder Arthroscopy – SLAP + Cuff + Biceps + AC" bundle...',
        'Building "Total Knee Arthroplasty (TKA)" bundle...',
        'Validating assistant surgeon necessity patterns...',
        '6 bundles created — ready for surgeon review',
      ],
      currentDetail: 0,
    },
    {
      id: 'payer',
      title: 'Payer Logic Mapping',
      subtitle: 'Syncing site-specific authorization rules',
      icon: Network,
      status: 'pending',
      progress: 0,
      details: [
        'Loading payer-specific auth rules...',
        'Syncing Anthem/BCBS prior auth requirements...',
        'Syncing UnitedHealthcare surgical criteria...',
        'Mapping Ohio BWC fee schedule to procedure catalog...',
        'Identifying payer-specific documentation thresholds...',
        'Cross-referencing denial patterns from 837 history...',
        'Payer rules synced — 4 payers, 12 plan variants mapped',
      ],
      currentDetail: 0,
    },
  ]);

  const allComplete = stages.every((s) => s.status === 'complete');

  const tick = useCallback(() => {
    setStages((prev) => {
      const next = [...prev];
      // Find current processing or first pending
      let activeIdx = next.findIndex((s) => s.status === 'processing');
      if (activeIdx === -1) {
        activeIdx = next.findIndex((s) => s.status === 'pending');
        if (activeIdx === -1) return prev; // all done
        next[activeIdx] = { ...next[activeIdx], status: 'processing' };
      }

      const stage = { ...next[activeIdx] };
      stage.progress = Math.min(stage.progress + Math.random() * 8 + 2, 100);
      
      const detailThreshold = ((stage.currentDetail + 1) / stage.details.length) * 100;
      if (stage.progress >= detailThreshold && stage.currentDetail < stage.details.length - 1) {
        stage.currentDetail++;
      }

      if (stage.progress >= 100) {
        stage.status = 'complete';
        stage.progress = 100;
        stage.currentDetail = stage.details.length - 1;
      }

      next[activeIdx] = stage;
      return next;
    });
  }, []);

  useEffect(() => {
    if (allComplete) return;
    const interval = setInterval(tick, 400);
    return () => clearInterval(interval);
  }, [allComplete, tick]);

  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="relative overflow-hidden rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-transparent p-6">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Cpu className="w-7 h-7 text-primary animate-pulse" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-foreground">LINK Customization Engine</h2>
            <p className="text-sm text-muted-foreground">
              {allComplete
                ? 'All processing complete — your profile is ready.'
                : 'Analyzing your clinical data to build a personalized configuration...'}
            </p>
          </div>
        </div>
        {/* Decorative glow */}
        <div className="absolute -right-20 -top-20 w-60 h-60 rounded-full bg-primary/5 blur-3xl pointer-events-none" />
      </div>

      {/* Processing stages */}
      <div className="space-y-4">
        {stages.map((stage) => {
          const Icon = stage.icon;
          return (
            <Card key={stage.id} className={`border transition-all duration-500 ${
              stage.status === 'complete' ? 'border-primary/30 bg-primary/[0.03]' :
              stage.status === 'processing' ? 'border-primary/20' :
              'border-border opacity-60'
            }`}>
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                    stage.status === 'complete' ? 'bg-primary/20' :
                    stage.status === 'processing' ? 'bg-primary/10' :
                    'bg-secondary'
                  }`}>
                    {stage.status === 'complete' ? (
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                    ) : stage.status === 'processing' ? (
                      <Loader2 className="w-5 h-5 text-primary animate-spin" />
                    ) : (
                      <Icon className="w-5 h-5 text-muted-foreground" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold text-foreground">{stage.title}</h3>
                        <p className="text-xs text-muted-foreground">{stage.subtitle}</p>
                      </div>
                      <Badge variant={stage.status === 'complete' ? 'default' : 'secondary'} className="text-[10px]">
                        {stage.status === 'complete' ? 'Complete' :
                         stage.status === 'processing' ? `${Math.round(stage.progress)}%` :
                         'Queued'}
                      </Badge>
                    </div>

                    {stage.status !== 'pending' && (
                      <>
                        <Progress value={stage.progress} className="h-1.5" />
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <FileSearch className="w-3 h-3 shrink-0" />
                          <span className="truncate font-mono">
                            {stage.details[stage.currentDetail]}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Complete CTA */}
      {allComplete && (
        <div className="flex justify-end">
          <Button onClick={onComplete} size="lg" className="gap-2">
            <Shield className="w-4 h-4" />
            View Surgeon Welcome Summary
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
